import { zodResolver } from "@hookform/resolvers/zod";
import { createContext, useContext, useMemo, useState } from "react";
import type { UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { FormCheckboxField } from "@/components/base/form/form-checkbox-field";
import {
	Form,
	FormField,
	SuppressInlineErrors,
	type UseHookFormProps,
	useHookForm,
} from "@/components/base/form/hook-form";
import { useFormErrorToasts } from "@/hooks/use-form-error-toasts";
import type { RateFormSchema } from "@/components/opportunities/opportunity-set-rate-form";
import { cx } from "@/utils/cx";
import type { OpportunityDto } from "@fp/admin-api-client/types/opportunityDto.js";
import { patchOpportunity } from "@fp/admin-api-client/client";
import { HttpStatusCode } from "axios";
import { FormTextAreaField } from "../base/form/form-textarea-field";
import { ProofOfWorkSection } from "./proof-of-work-section";
import { referenceRequirementsSchema, toCreateReferenceRequirementDtos } from "./reference-requirement-schema";

export { toReferenceRequirementFormValues } from "./reference-requirement-schema";

export const ProofOfWorkFormSchema = z
	.object({
		id: z.string(),
		requireProofOfWork: z.boolean(),
		requireDescription: z.boolean(),
		requireImage: z.boolean(),
		requireReferenceId: z.boolean(),
		referenceRequirements: referenceRequirementsSchema,
		instructions: z.string().optional(),
	})
	.refine(
		(values) =>
			!values.requireReferenceId ||
			!values.requireProofOfWork ||
			values.referenceRequirements.length > 0,
		{
			message: "At least one reference requirement is required",
			path: ["referenceRequirements"],
		},
	);

export type ProofOfWorkFormSchema = z.infer<typeof ProofOfWorkFormSchema>;

export type ProofOfWorkFormContext = {
	form: UseFormReturn<ProofOfWorkFormSchema>;
	handleSubmit: ReturnType<UseFormHandleSubmit<RateFormSchema>>;
	transition: boolean;
	errorMessage: string | undefined;
};

export const ProofOfWorkFormContext = createContext<
	ProofOfWorkFormContext | undefined
>(undefined);

export const DEFAULT_VALUES = {
	id: "",
	requireProofOfWork: true,
	requireImage: false,
	requireDescription: false,
	requireReferenceId: false,
	referenceRequirements: [],
	instructions: "",
} as ProofOfWorkFormSchema;

export const useProofOfWorkForm = () => {
	const context = useContext(ProofOfWorkFormContext);
	if (!context) {
		throw new Error("ProofOfWorkFormContext not found");
	}
	return context;
};

export type ProofOfWorkFormProps = UseHookFormProps<
	ProofOfWorkFormSchema,
	OpportunityDto
> & {
	defaultValues?: ProofOfWorkFormSchema;
	children:
		| ((context: ProofOfWorkFormContext) => React.ReactNode)
		| React.ReactNode;
};

export const ProofOfWorkForm = ({
	defaultValues = DEFAULT_VALUES,
	onSubmit = ProofOfWorkFormSubmitHandler,
	children,
	...props
}: ProofOfWorkFormProps) => {
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);
	const { form, handleSubmit, transition } = useHookForm<
		ProofOfWorkFormSchema,
		OpportunityDto
	>({
		resolver: zodResolver(ProofOfWorkFormSchema),
		defaultValues: {
			...DEFAULT_VALUES,
			...defaultValues,
		},
		onSuccess: async (data) => {
			setErrorMessage(undefined);
			await props.onSuccess?.(data);
		},
		onError: async (errors) => {
			setErrorMessage("Unable to save proof of work");
			await props.onError?.(errors);
		},
		onSubmit,
		...props,
	});

	const context = useMemo(
		() => ({ form, handleSubmit, transition, errorMessage }),
		[form, handleSubmit, transition, errorMessage],
	);

	return (
		<ProofOfWorkFormContext.Provider value={context}>
			<Form {...form}>
				{typeof children === "function" ? children(context) : children}
			</Form>
		</ProofOfWorkFormContext.Provider>
	);
};

export const ProofOfWorkFormErrorMessage = () => {
	const { errorMessage } = useProofOfWorkForm();

	if (!errorMessage) return null;

	return <p className="text-error-primary">{errorMessage}</p>;
};

export const ProofOfWorkFormSubmitHandler = async (
	formValues: ProofOfWorkFormSchema,
) => {
	const result = await patchOpportunity(formValues.id, {
		requireImage: formValues.requireProofOfWork
			? formValues.requireImage
			: false,
		requireDescription: formValues.requireProofOfWork
			? formValues.requireDescription
			: false,
		referenceRequirements:
			formValues.requireProofOfWork && formValues.requireReferenceId
				? toCreateReferenceRequirementDtos(formValues.referenceRequirements)
				: [],
		payoutInstructions: formValues.instructions,
	} as Parameters<typeof patchOpportunity>[1]);

	if (result.status !== HttpStatusCode.Ok) {
		return {
			success: false as const,
			error: new Error("Unable to update opportunity"),
		};
	}

	return { success: true as const, data: result.data };
};

export const ProofOfWorkFormContent = ({
	className,
}: {
	className?: string;
}) => {
	const { form } = useProofOfWorkForm();

	useFormErrorToasts(form.control);

	const requireProofOfWork = form.watch("requireProofOfWork");
	const requireReferenceId = form.watch("requireReferenceId");

	return (
		<SuppressInlineErrors>
			<div className={cx("flex flex-col gap-4", className)}>
				<p className="text-md text-quaternary">
					Require the following Proof of Work from talent members upon requesting payout.
				</p>
				<FormField
					control={form.control}
					name="requireProofOfWork"
					render={({ field }) => (
						<FormCheckboxField
							field={field}
							label="Require Proof of Work"
							inputClasses="w-fit"
						/>
					)}
				/>
				<ProofOfWorkSection
					control={form.control}
					fieldNames={{
						requireImage: "requireImage",
						requireDescription: "requireDescription",
						requireReferenceId: "requireReferenceId",
						referenceRequirements: "referenceRequirements",
					}}
					requireProofOfWork={requireProofOfWork}
					requireReferenceId={requireReferenceId}
				/>
				<div className="mt-4">
					<FormField
						control={form.control}
						name="instructions"
						render={({ field }) => (
							<div className="flex flex-col gap-2">
								<FormTextAreaField
									field={field}
									label="Instructions for proof of work (optional)"
									placeholder="Provide any specific instructions for the proof of work submission."
									rows={4}
								/>
							</div>
						)}
					/>
				</div>
			</div>
		</SuppressInlineErrors>
	);
};
